import React, { useId } from 'react';
import * as styles from './LogoLoader.module.scss';

// Shared by the payment portal and the landing page; each app wraps it to supply
// localized `label` and `phrases`.
const {
  logoLoader,
  mark,
  phrase,
  phraseSet,
  phraseSlot,
  phraseWord,
  phraseLetter,
  ringStroke,
  ringSettle,
  leaf,
  tipGrowth,
  vein,
  visuallyHidden
} = styles;

const cn = (...classNames) => classNames.filter(Boolean).join(' ');

const BLUE = '#003C8F';
const GREEN = '#92BF45';

const RING_PATH =
  'M28.9396 9.49243C28.5159 10.0705 27.8419 10.8221 27.3796 11.3616C25.6463 13.4235 23.8167 15.4083 21.8522 17.2582C19.2907 19.6862 16.5751 21.9022 13.5322 23.6751C11.8181 24.6578 10.0462 25.5057 8.08179 25.8718C7.15734 26.0453 6.25216 26.0838 5.32771 25.9297C3.80622 25.6599 2.7277 24.8313 1.99585 23.4824C1.57214 22.6923 1.34103 21.8444 1.20621 20.9773C1.01362 19.8404 0.878805 18.6842 0.917324 17.528C1.05214 13.5198 2.32325 9.91637 4.88474 6.79464C7.65809 3.40314 11.2596 1.32199 15.6122 0.589734C18.9633 0.0309074 22.1989 0.454845 25.3381 1.78447C27.4374 2.67088 29.2863 3.92343 30.8848 5.56137C33.5041 8.23988 35.1411 11.4194 35.7382 15.1385C36.3352 18.8576 35.7574 22.4225 34.0819 25.7948C33.35 27.2785 32.4063 28.6274 31.27 29.8414C28.6507 32.6163 25.4922 34.4084 21.7366 35.1214C17.2877 35.9693 13.1277 35.1599 9.25661 32.8475C8.12031 32.1731 7.08031 31.3445 6.1366 30.4003C5.65512 29.9378 5.36623 29.3597 5.26993 28.7045C5.21215 28.2806 5.30845 27.8759 5.44326 27.4905C5.50104 27.3171 5.63586 27.2207 5.82845 27.2207C5.86697 27.2207 5.92475 27.2207 5.96327 27.2207C7.85068 27.3171 9.62254 26.8161 11.3366 26.0838C11.664 25.9489 11.9337 25.9297 12.2418 26.1031C12.7811 26.3729 13.3203 26.6234 13.8788 26.8739C13.9559 26.9124 14.0137 26.951 14.0907 26.9702C14.3218 27.0473 14.5144 26.9895 14.6107 26.7968C14.707 26.6234 14.6492 26.3921 14.4566 26.2765C14.2833 26.1609 14.0714 26.0645 13.8788 25.9682C13.6092 25.8333 13.3203 25.6984 13.0507 25.5635C12.9351 25.5057 12.9159 25.4094 13.0314 25.313C13.0892 25.2745 13.1277 25.2552 13.1855 25.2167C13.8018 24.8698 14.4181 24.5229 15.0151 24.1568C15.2463 24.0219 15.4388 24.0027 15.6507 24.1183C15.9203 24.2532 16.2092 24.3688 16.4789 24.5037C17.6922 25.1396 18.2892 26.4114 18.0003 27.7218C17.7307 28.9743 16.6137 29.9185 15.2848 29.9763C14.7263 30.0149 14.187 29.8222 13.6863 29.5909C12.5692 29.0706 11.4522 28.5504 10.3351 28.0108C9.60328 27.6639 8.87142 27.7025 8.15883 28.0879C8.08179 28.1264 8.02401 28.165 7.96623 28.2035C7.38845 28.6274 7.34994 29.2055 7.8892 29.6873C8.77513 30.4773 9.71883 31.1518 10.7396 31.7106C12.55 32.7127 14.4951 33.3293 16.5559 33.5605C19.31 33.8881 21.9678 33.4835 24.51 32.4043C26.4167 31.595 28.1115 30.4581 29.5559 28.9743C32.0211 26.4499 33.5041 23.4438 33.9471 19.956C34.4671 15.7744 33.4848 11.959 31.0196 8.56747C28.9011 5.65772 26.0507 3.73073 22.5841 2.74796C20.7929 2.22768 18.9826 2.07352 17.1337 2.20841C13.59 2.49745 10.4892 3.82708 7.83142 6.19728C5.15437 8.62528 3.51733 11.7277 2.90103 15.3697C2.53511 17.528 2.65066 19.6669 3.18992 21.7866C3.51733 23.0777 4.44178 23.81 5.75141 23.9449C6.88771 24.0605 7.98549 23.8485 9.06402 23.5209C10.8744 22.9621 12.55 22.1528 14.187 21.2085C17.3455 19.3972 20.2729 17.2389 23.0463 14.8687C24.8952 13.2886 26.6863 11.6314 28.3811 9.89709C28.4774 9.80075 28.7663 9.5117 28.8819 9.43462C29.1515 9.16484 29.113 9.26119 28.9396 9.49243Z';

// Centerline of the ring shape, starting at the stem's inner tip, so a single
// stroke along it reveals the mark as one continuous line.
const RING_CENTERLINE =
  'M28.94 9.45L27.9 10.6C25 13.6 18 19.5 13.86 22.44C11.5 23.9 9.6 24.6 8.57 24.7C7.2 24.9 6.3 25.05 5.54 24.9C4.2 24.6 3.1 23.6 2.6 22.6C2 21.4 1.75 19.5 1.75 17.85A16.6 16.6 0 1 1 6.61 29.59C5.9 28.9 6.4 27.7 8 27.45C9.2 27.25 10 27.2 11 27.25C12.4 27.4 13.2 28.1 14.3 28.25C15.4 28.4 16.3 28.2 16.6 27.2C16.9 26.1 16.3 25.3 15.4 25.1C14.7 24.95 14.1 25 13.6 25.2';

// Leaves are drawn as solid silhouettes; the divider (vein) is cut in by a mask
// stroke that starts outside the leaf base and runs to the rounded end of the slit.
// Ordered base-of-stem to tip so the leaves sprout in sequence, alternating sides.
// `tip` splits a leaf's flared end off its body so it can grow out on its own along
// `growth`; the tip clip overlaps the body slightly so no anti-aliased seam shows at rest.
const LEAVES = [
  {
    fill: GREEN,
    d: 'M5.88499 19.6086C5.88499 18.7222 5.86573 17.8165 5.88499 16.9301C5.90425 15.6198 6.65537 14.5599 7.84944 14.136C9.6213 13.5193 11.5858 14.849 11.6243 16.7374C11.6435 18.0863 11.6243 19.4545 11.6435 20.8034C11.6435 21.1309 11.528 21.3044 11.2391 21.4393C10.7191 21.6705 10.1991 21.9017 9.65982 22.0944L7.75315 22.6533C7.29092 22.7496 6.80944 22.8074 6.32796 22.8267C5.94277 22.846 5.88499 22.7689 5.88499 22.3835C5.88499 21.4585 5.88499 20.5336 5.88499 19.6086Z',
    vein: 'M8.82 23.4V17.99',
    veinWidth: 0.83
  },
  {
    fill: GREEN,
    d: 'M12.8955 14.7526C12.8955 13.0375 12.9147 11.3225 12.8762 9.62676C12.857 8.48984 13.7621 7.31437 14.9562 7.08313C16.3622 6.81336 17.8066 7.8732 17.9029 9.29917C17.9029 9.41479 17.9029 9.54968 17.9029 9.6653C17.9029 12.074 17.9029 14.4635 17.9029 16.8722C17.9029 17.2576 17.7873 17.5081 17.4599 17.7201C17.017 17.9899 16.5933 18.2982 16.1503 18.5873L14.4747 19.6664C14.051 19.8783 13.6466 20.1481 13.2229 20.3601C12.934 20.5143 12.8955 20.4757 12.8762 20.1481C12.8762 20.0132 12.8762 19.8783 12.8762 19.7242C12.8955 18.0862 12.8955 16.4098 12.8955 14.7526Z',
    vein: 'M15.39 20.4V12.41',
    veinWidth: 0.86
  },
  {
    fill: BLUE,
    d: 'M24.6243 26.6609C24.2199 26.6801 23.8539 26.5645 23.488 26.4103C21.6584 25.5625 19.8287 24.6953 18.0184 23.8474C17.6332 23.674 17.2672 23.5006 16.8821 23.3272C16.6124 23.1923 16.5932 23.1152 16.8243 22.9418C17.3058 22.5756 17.7872 22.2288 18.2687 21.8626C18.4421 21.7278 18.6154 21.7856 18.7887 21.8626L19.5206 21.3038C19.2702 21.1689 19.2702 21.1111 19.4821 20.8991C19.5591 20.8221 19.6361 20.7643 19.7324 20.6872C20.0213 20.4367 20.3295 20.2054 20.6184 19.9357C20.8687 19.7044 21.0806 19.6659 21.408 19.82C22.2169 20.2247 23.0258 20.5716 23.8347 20.957C24.4895 21.2653 25.1443 21.5543 25.7991 21.8819C26.7428 22.3829 27.2436 23.1923 27.2051 24.2521C27.1665 25.3312 26.5695 26.0828 25.568 26.5067C25.2599 26.6416 24.9325 26.6801 24.6243 26.6609Z',
    vein: 'M18.06 21.07L22.04 22.93',
    veinWidth: 0.8
  },
  {
    fill: GREEN,
    d: 'M21.928 14.2128L20.5992 15.3883C20.1947 15.6773 19.8288 15.9856 19.4243 16.294C19.2125 16.4481 19.1354 16.4096 19.0969 16.1591C19.0969 16.1013 19.0969 16.0435 19.0969 15.9664C19.0969 13.153 19.0969 10.3203 19.0969 7.50688C19.0969 6.58193 19.6554 5.81113 20.5029 5.48354C21.9088 4.94398 23.4303 5.92675 23.4495 7.4298C23.4688 8.16206 23.4495 8.87505 23.4495 9.6073C23.4495 9.72292 23.4495 9.81927 23.4495 9.93489C23.4688 10.1661 23.5266 10.2047 23.7384 10.089C24.2584 9.78073 24.7592 9.45314 25.2599 9.12555C25.8762 8.70162 26.531 8.43184 27.2629 8.35476C27.4747 8.33549 27.6866 8.31622 27.8984 8.37403C28.0333 8.41257 28.0718 8.48965 27.9755 8.60527C27.937 8.66308 27.8792 8.72089 27.8214 8.7787C26.0303 10.6286 24.1621 12.4014 22.1977 14.0587C22.1399 14.1165 22.0821 14.2128 21.928 14.2128Z',
    vein: 'M21.43 15.8V10.2',
    veinWidth: 0.89,
    tip: {
      body: 'M-4 -4H23.45V40H-4Z',
      clip: 'M23.2 -4H40V40H23.2Z',
      growth: 'M22.9 11.5L28.4 8.3',
      growthWidth: 3.6
    }
  },
  {
    fill: BLUE,
    d: 'M27.6095 16.5834C28.3414 16.9303 29.0733 17.2579 29.7858 17.6047C30.4021 17.8938 30.8644 18.3177 31.0762 18.9729C31.3651 19.8208 31.1918 20.5723 30.5947 21.1889C29.9784 21.8441 29.2081 22.0368 28.3607 21.7863C27.8792 21.6514 27.4362 21.3816 26.974 21.1697C25.4718 20.476 23.9888 19.7822 22.5058 19.0693C22.4095 19.0307 22.3132 18.9729 22.2169 18.9344C22.0051 18.8188 22.0051 18.7609 22.1592 18.5875C22.4866 18.2599 22.8332 17.9516 23.1414 17.624C23.334 17.4313 23.5073 17.412 23.7577 17.5277L24.3932 16.9303C24.0851 16.7761 24.0658 16.7183 24.3162 16.4678C25.2021 15.5814 26.0303 14.6565 26.8584 13.7315C27.937 12.5175 28.977 11.2649 29.9977 9.99313C30.017 9.97386 30.0362 9.93532 30.0555 9.91606C30.3251 9.58847 30.4021 9.5692 30.6333 9.91606C31.4036 11.0722 31.5384 12.2863 30.9799 13.5581C30.6718 14.2711 30.171 14.8299 29.5162 15.2345C28.9384 15.6007 28.3799 15.9668 27.8021 16.3329C27.7444 16.3715 27.6481 16.41 27.6095 16.5834Z',
    vein: 'M23.13 16.79L26.71 18.46',
    veinWidth: 0.81,
    tip: {
      body: 'M-4 19.02L40 15.45V40H-4Z',
      clip: 'M-4 19.32L40 15.75V-4H-4Z',
      growth: 'M25.4 17.9C26.5 16 27.9 14.8 28.9 13.7C29.9 12.6 30.5 11.4 30.75 9.3',
      growthWidth: 4.6
    }
  }
];

const MASK_BOUNDS = { x: -4, y: -4, width: 44, height: 44 };

// Letters are grouped per word so the phrase still wraps between words; each letter
// gets its position in the phrase for the staggered float-up delay.
const FloatingText = ({ text }) => {
  let letterIndex = 0;

  return text.split(' ').map((word, wordIndex) => (
    <React.Fragment key={`${word}-${wordIndex}`}>
      {wordIndex > 0 ? ' ' : null}
      <span className={phraseWord}>
        {Array.from(word).map((char) => {
          const index = letterIndex;
          letterIndex += 1;
          return (
            <span key={index} className={phraseLetter} style={{ '--letter-index': index }}>
              {char}
            </span>
          );
        })}
      </span>
    </React.Fragment>
  ));
};

// Must match $phrase-count in LogoLoader.module.scss: the CSS shows phrase N during loop
// N of every $phrase-count loops.
const PHRASE_COUNT = 3;

const NO_PHRASES = [];

// `translations` ([{ locale, label, phrases }]) renders the text in every locale, each
// marked with data-loader-locale, for pages whose markup is rendered before the visitor's
// locale is known; the page's CSS shows only the active one. Otherwise `label` and
// `phrases` are used as given.
const LogoLoader = ({ size = 64, label = 'Loading', phrases = NO_PHRASES, translations, className = '' }) => {
  const idPrefix = `logo-loader-${useId().replace(/:/g, '')}`;
  const ringMaskId = `${idPrefix}-ring`;
  const texts = translations ?? [{ label, phrases }];
  const hasPhrases = texts.some((text) => text.phrases.length > 0);

  return (
    <div className={cn(logoLoader, className)} role="status" aria-live="polite" data-testid="logo-loader">
      <svg
        className={mark}
        style={{ width: size, height: size }}
        viewBox="0 0 36 36"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <mask id={ringMaskId} maskUnits="userSpaceOnUse" {...MASK_BOUNDS}>
            <path className={ringStroke} d={RING_CENTERLINE} pathLength="1" />
            <rect className={ringSettle} {...MASK_BOUNDS} fill="white" />
          </mask>
          {LEAVES.map(({ vein: veinPath, veinWidth, tip }, index) => {
            const leafId = index + 1;
            const veinCut = (
              <path className={vein} data-leaf={leafId} d={veinPath} pathLength="1" strokeWidth={veinWidth} />
            );

            return (
              <React.Fragment key={veinPath}>
                <mask id={`${idPrefix}-leaf-${leafId}`} maskUnits="userSpaceOnUse" {...MASK_BOUNDS}>
                  <rect {...MASK_BOUNDS} fill="white" />
                  {veinCut}
                </mask>
                {tip ? (
                  <>
                    <mask id={`${idPrefix}-tip-mask-${leafId}`} maskUnits="userSpaceOnUse" {...MASK_BOUNDS}>
                      <path
                        className={tipGrowth}
                        data-leaf={leafId}
                        d={tip.growth}
                        pathLength="1"
                        strokeWidth={tip.growthWidth}
                      />
                      {veinCut}
                    </mask>
                    <clipPath id={`${idPrefix}-body-${leafId}`}>
                      <path d={tip.body} />
                    </clipPath>
                    <clipPath id={`${idPrefix}-tip-${leafId}`}>
                      <path d={tip.clip} />
                    </clipPath>
                  </>
                ) : null}
              </React.Fragment>
            );
          })}
        </defs>
        <path d={RING_PATH} fill={BLUE} mask={`url(#${ringMaskId})`} />
        {LEAVES.map(({ d, fill, tip }, index) => {
          const leafId = index + 1;

          return (
            <g key={d} className={leaf} data-leaf={leafId}>
              <path
                d={d}
                fill={fill}
                mask={`url(#${idPrefix}-leaf-${leafId})`}
                clipPath={tip ? `url(#${idPrefix}-body-${leafId})` : undefined}
              />
              {tip ? (
                <path
                  d={d}
                  fill={fill}
                  mask={`url(#${idPrefix}-tip-mask-${leafId})`}
                  clipPath={`url(#${idPrefix}-tip-${leafId})`}
                />
              ) : null}
            </g>
          );
        })}
      </svg>
      {hasPhrases ? (
        <p className={phrase} aria-hidden="true" data-testid="logo-loader-phrase">
          {texts.map(({ locale, phrases: localePhrases }) => (
            <span key={locale ?? 'phrases'} className={phraseSet} lang={locale} data-loader-locale={locale}>
              {localePhrases.slice(0, PHRASE_COUNT).map((text, index) => (
                <span key={index} className={phraseSlot} style={{ '--phrase-index': index }}>
                  <FloatingText text={text} />
                </span>
              ))}
            </span>
          ))}
        </p>
      ) : null}
      {texts.map(({ locale, label: localeLabel }) => (
        <span key={locale ?? 'label'} className={visuallyHidden} lang={locale} data-loader-locale={locale}>
          {localeLabel}
        </span>
      ))}
    </div>
  );
};

export default LogoLoader;
